import React, { useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { useAuditLogsTable } from '../hooks/useAuditLogsTable';
import { ENTITY_TYPES, getActionColor, getActionLabel, getEntityTypeLabel } from '../model/auditLogConstants';
import AuditLogDetailsDialog from './AuditLogDetailsDialog';

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

export default function AuditLogsTable() {
  const {
    rows, total, loading, page, setPage, rowsPerPage, setRowsPerPage,
    filters, setFilter, resetFilters, actionOptions,
  } = useAuditLogsTable();
  const [detailsLog, setDetailsLog] = useState(null);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>Registro de auditoría</Typography>
        <Typography sx={{ mt: 0.5, color: '#6b7280' }}>
          Historial de acciones sensibles realizadas en el sistema: altas y bajas de usuarios, cambios de rol y permisos, e intentos de inicio de sesión.
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e3e8ef', mb: 3 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={4} md={2.5}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={filters.action}
              onChange={(e) => setFilter('action', e.target.value)}
              sx={inputSx}
            >
              <MenuItem value="">Toda acción</MenuItem>
              {actionOptions.map((action) => (
                <MenuItem key={action} value={action}>{getActionLabel(action)}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6} sm={4} md={2.5}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={filters.entityType}
              onChange={(e) => setFilter('entityType', e.target.value)}
              sx={inputSx}
            >
              <MenuItem value="">Toda entidad</MenuItem>
              {ENTITY_TYPES.map((entityType) => (
                <MenuItem key={entityType} value={entityType}>{getEntityTypeLabel(entityType)}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="ID usuario"
              value={filters.userId}
              onChange={(e) => setFilter('userId', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2.25}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Desde"
              InputLabelProps={{ shrink: true }}
              value={filters.from}
              onChange={(e) => setFilter('from', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2.25}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Hasta"
              InputLabelProps={{ shrink: true }}
              value={filters.to}
              onChange={(e) => setFilter('to', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs="auto" sx={{ ml: 'auto' }}>
            <Tooltip title="Restablecer filtros">
              <IconButton onClick={resetFilters} sx={{ border: '1px solid #d0d7de', borderRadius: 2 }}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', borderRadius: 3, border: '1px solid #e3e8ef' }}>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#f5f8ff', color: '#374151', fontWeight: 700, borderBottom: '2px solid #e3e8ef' } }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Entidad</TableCell>
                <TableCell>ID entidad</TableCell>
                <TableCell>IP</TableCell>
                <TableCell align="right">Detalle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <Stack alignItems="center" spacing={1} sx={{ color: '#9ca3af' }}>
                      <HistoryToggleOffIcon sx={{ fontSize: 40 }} />
                      <Typography sx={{ color: '#6b7280' }}>No hay eventos con estos filtros</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ color: '#374151', whiteSpace: 'nowrap' }}>{new Date(row.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    {row.user ? (
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }} noWrap>{row.user.nombre}</Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }} noWrap>{row.user.email}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>{row.userId ? `ID ${row.userId}` : '—'}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={getActionColor(row.action)} label={getActionLabel(row.action)} sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>{getEntityTypeLabel(row.entityType)}</TableCell>
                  <TableCell sx={{ color: '#6b7280' }}>{row.entityId ?? '—'}</TableCell>
                  <TableCell sx={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{row.ip ?? '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver detalle">
                      <IconButton size="small" onClick={() => setDetailsLog(row)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
        />
      </Paper>

      <AuditLogDetailsDialog log={detailsLog} open={!!detailsLog} onClose={() => setDetailsLog(null)} />
    </Box>
  );
}
